import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order, OrderStatus } from '@runninglime/shared';
import { apiGet, apiPut } from '../../lib/api.ts';
import { formatDate, formatPrice } from '../../lib/utils.ts';
import { StatusBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

const STATUS_TABS: { value: OrderStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'problem', label: 'Problem' },
];

const NEXT_STATUS: Record<string, OrderStatus> = {
  pending: 'confirmed',
  confirmed: 'packed',
  packed: 'shipped',
  shipped: 'delivered',
};

export default function OrderManager() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const load = async () => {
    const q = filter ? `?status=${filter}` : '';
    const res = await apiGet<{ items: Order[]; total: number }>(`/admin/orders${q}`);
    if (res.success && res.data) setOrders(res.data.items);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const advanceStatus = async (orderId: string, newStatus: OrderStatus) => {
    setUpdating(orderId);
    await apiPut(`/admin/orders/${orderId}/status`, { status: newStatus });
    await load();
    setUpdating(null);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-6">Orders</h1>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              filter === tab.value
                ? 'bg-lime text-dark shadow-md'
                : 'bg-white text-gray-600 hover:bg-lime/20 border border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-400 text-sm">No orders found</td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50/50 transition">
                    <td className="px-6 py-4">
                      <Link to={`/admin/orders/${o.id}`} className="font-mono text-sm text-green hover:underline">
                        #{o.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{o.userId.slice(0, 8)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{o.items.length}</td>
                    <td className="px-6 py-4 text-sm font-medium text-dark">{formatPrice(o.total)}</td>
                    <td className="px-6 py-4"><StatusBadge status={o.status} /></td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(o.createdAt)}</td>
                    <td className="px-6 py-4 text-right">
                      {NEXT_STATUS[o.status] && (
                        <button
                          onClick={() => advanceStatus(o.id, NEXT_STATUS[o.status])}
                          disabled={updating === o.id}
                          className="text-xs bg-lime/20 text-dark px-3 py-1.5 rounded-lg hover:bg-lime/40 transition font-medium disabled:opacity-50"
                        >
                          {updating === o.id ? '...' : `→ ${NEXT_STATUS[o.status]}`}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
